import { useMemo, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { cn } from '../../../../lib/cn.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { CommissionDetailPanel } from '../../../../components/admin/CommissionDetailPanel.js';
import {
  useCommissionOverview,
  type CommissionOverviewCollege,
} from '../../../../hooks/useCommission.js';
import {
  canViewCommissionConsole,
  commissionSummary,
  formatCommissionDate,
  matchesCollegeQuery,
  overrideCount,
  overrideCountLabel,
} from '../../../../lib/commission.js';
import { Input } from '../../../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/commission',
  // Gates on the edit_commission CAPABILITY (Brief 8b) — same audience as
  // before, since that switch is exactly admin + super_admin's preset, but it
  // now follows the switch rather than the role. See its note on owner/accounts,
  // who hold view_commission but would meet an editor they cannot save. The
  // server's capability guard stays the real gate.
  beforeLoad: () => {
    if (!canViewCommissionConsole(useAuthStore.getState().user)) {
      throw redirect({ to: '/app' });
    }
  },
  component: CommissionPage,
});

function CollegeList({
  colleges,
  isLoading,
  error,
  onRetry,
  selectedId,
  onSelect,
}: {
  colleges: CommissionOverviewCollege[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      colleges
        .filter((c) => matchesCollegeQuery(c, search))
        .sort((a, b) => a.providerName.localeCompare(b.providerName)),
    [colleges, search],
  );

  return (
    <div className="surface p-4">
      <Input
        placeholder="Search college…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading colleges…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn&apos;t load colleges: {error.message}{' '}
            <button className="underline" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No colleges match.</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <ul className="divide-y divide-line">
            {filtered.map((c) => {
              const specials = overrideCountLabel(overrideCount(c.courses));
              return (
                <li key={c.providerId}>
                  <button
                    onClick={() => onSelect(c.providerId)}
                    className={cn(
                      'flex w-full items-center gap-3 px-1 py-2.5 text-left transition',
                      selectedId === c.providerId ? 'bg-cream-deep/40' : 'hover:bg-cream/60',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{c.providerName}</span>
                      <span className="block truncate text-xs text-muted">
                        {commissionSummary(c.current)}
                      </span>
                    </span>
                    {specials && (
                      <span className="badge shrink-0 bg-cream-deep/60 text-muted">{specials}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The commission console (Brief 6b): every college's current deal at a glance,
 * with the selected college's deal, history and per-course special deals beside
 * it. Selection is local state, not a URL param — the pricing console precedent.
 */
export function CommissionPage() {
  const { data, isLoading, error, refetch } = useCommissionOverview();
  const colleges = data?.colleges ?? [];
  const asOf = data?.asOf ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = colleges.find((c) => c.providerId === selectedId) ?? null;

  return (
    <div>
      <AdminPageHeader
        title="Commission"
        description="What each college pays your agency, and any special deals on individual courses. Every change is effective-dated and kept in the history."
      />
      {asOf && (
        <p className="mb-4 text-xs text-muted">Deals current as of {formatCommissionDate(asOf)}.</p>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start">
        <CollegeList
          colleges={colleges}
          isLoading={isLoading}
          error={(error as Error | null) ?? null}
          onRetry={() => refetch()}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {/* key remounts the panel so its form re-initialises from the new
            college. asOf is required by the panel (the Current badge and the
            date default); if it is somehow absent we show the placeholder
            rather than guess a date. */}
        {selected && asOf ? (
          <CommissionDetailPanel key={selected.providerId} college={selected} asOf={asOf} />
        ) : (
          <div className="surface p-10 text-center text-sm text-muted">
            Select a college to view and set its commission deal.
          </div>
        )}
      </div>
    </div>
  );
}
